<template>
  <div class="max-w-6xl p-6 mx-auto md:p-8">
    <!-- Page header -->
    <div>
      <h2 class="text-2xl font-semibold text-gray-800">Corporate Reports</h2>
      <p class="text-sm text-gray-500">
        Filter by date and export a batch report for the corporates you choose
      </p>
    </div>

    <div class="mt-6 space-y-4">
      <!-- Filters card -->
      <section class="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm">
        <div class="flex items-center mb-4 space-x-1 text-gray-500">
          <span class="text-base material-icons-round">filter_list</span>
          <span class="text-xs font-semibold tracking-widest uppercase">Filters</span>
        </div>

        <div class="flex flex-wrap items-end gap-4">
          <!-- Date range -->
          <div class="w-full sm:w-auto">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Date Range</label>
            <div class="flex items-center h-10 mt-1.5 bg-white border border-gray-300 shadow-sm rounded-xl">
              <span class="pl-3 pr-1 text-base text-gray-500 material-icons-round">event</span>
              <v-date-picker v-model="filters.range" is-range>
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="flex items-center">
                    <input
                      :value="inputValue.start"
                      v-on="inputEvents.start"
                      placeholder="From"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                    <span class="mx-1 text-base text-gray-300 material-icons-round">arrow_forward</span>
                    <input
                      :value="inputValue.end"
                      v-on="inputEvents.end"
                      placeholder="To"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                  </div>
                </template>
              </v-date-picker>
              <button
                v-if="filters.range.start || filters.range.end"
                @click="filters.range = { start: null, end: null }"
                type="button"
                title="Clear dates"
                class="flex items-center justify-center w-8 h-8 mx-1 text-gray-400 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none"
              >
                <span class="text-lg material-icons-round">close</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Export card -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header
          class="flex flex-wrap items-center justify-between gap-4 px-6 py-4 border-b border-gray-100"
        >
          <div class="flex items-start">
            <span
              class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
            >
              apartment
            </span>
            <div>
              <h3 class="text-base font-semibold text-gray-800">Batch Export</h3>
              <p class="text-sm text-gray-500">
                Choose corporates to include in the export
              </p>
            </div>
          </div>
          <export-batch-corporates
            :working="loading"
            :corporates="corporatesList"
            :filters="filters"
          ></export-batch-corporates>
        </header>

        <div class="overflow-x-auto">
          <table class="min-w-full leading-normal">
            <thead>
              <tr>
                <th
                  class="px-6 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase bg-gray-50 border-b border-gray-200"
                >
                  Corporate
                </th>
                <th
                  class="px-6 py-3 text-xs font-semibold tracking-wider text-right text-gray-500 uppercase bg-gray-50 border-b border-gray-200"
                >
                  <label class="inline-flex items-center gap-2 cursor-pointer">
                    <span>Include all</span>
                    <input
                      type="checkbox"
                      v-model="selectAll"
                      class="text-taxiye-500 border-gray-300 rounded form-checkbox focus:border-taxiye-300"
                    />
                  </label>
                </th>
              </tr>
            </thead>
            <tbody v-if="corporatesList.length" class="relative">
              <tr
                v-for="corporate in corporatesList"
                :key="corporate.fieldName"
                class="transition-colors duration-150 hover:bg-gray-50"
              >
                <td class="px-6 py-4 text-sm text-gray-800 border-b border-gray-100">
                  <label class="flex items-center cursor-pointer whitespace-no-wrap">
                    {{ corporate.label }}
                  </label>
                </td>
                <td class="px-6 py-4 text-sm text-right border-b border-gray-100">
                  <input
                    type="checkbox"
                    v-model="corporate.value"
                    class="text-taxiye-500 border-gray-300 rounded form-checkbox focus:border-taxiye-300"
                  />
                </td>
              </tr>
              <tr v-if="loading">
                <td colspan="2" class="p-0">
                  <div
                    class="flex items-center justify-center w-full py-8 bg-white bg-opacity-75"
                  >
                    <i class="text-4xl text-taxiye-500 material-icons-round spin">sync</i>
                  </div>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="2" class="py-10 text-center bg-white">
                  <div v-if="loading" class="inline-flex flex-col items-center text-gray-400">
                    <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                    <span class="mt-2 text-sm">Loading corporates…</span>
                  </div>
                  <div v-else class="inline-flex flex-col items-center text-gray-400">
                    <i class="text-5xl material-icons-round">domain_disabled</i>
                    <span class="mt-2 text-sm font-medium">No corporates found</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import ExportBatchCorporates from "@/components/ExportBatchCorporates";
// import AppModules from "@/components/AppModules";

export default {
  data() {
    return {
      tripRequests: [],
      openDropdown: false,
      selectedAccount: "",
      loading: true,
      accounts: [],
      accountId: null,
      selectedFilter: "all",

      selectedDriver: "",

      driverSearchResults: [],
      userSearchResults: [],
      passengerSearchResults: [],
      selectedPassenger: "",

      selectedUser: "",

      driverId: null,

      userId: null,

      passengerId: null,

      range: {
        start: null,
        end: null,
      },

      statuses: ["IN_REQUEST", "CANCELLED", "EXPIRED", "DECLINED"],

      appModules: [],

      corporatesList: [],


      filters: {
        driverId: null,

        userId: null,

        passengerId: null,

        range: {
          start: null,
          end: null,
        },
      }

    };
  },
  components: {
    ExportBatchCorporates,
    // AppModules,
  },
  watch: {
    selectAll(newValue) {
      this.appModules.forEach(x => x.value = newValue);
    },
    selectedFilter() {
      this.getTripRequests();
    },
    range: {
      handler() {
        this.getTripRequests();
      },
      deep: true,
    },
    $route() {
      if (this.$route.query.status) {
        this.selectedFilter = this.$route.query.status;
      }
    },
  },
  created() {
    // var sd = new Date();
    // sd.setDate(0);
    // sd.setDate(1);
    // var ed = new Date();
    // ed.setMonth(ed.getMonth() + 1);
    // ed.setDate(0);
    // this.range.start = sd;
    // this.range.end = ed;

    if (this.$route.query.status) {
      this.selectedFilter = this.$route.query.status;
    }
    if (this.$route.query.page) {
      this.currentPage = this.$route.query.page;
    }
    this.$http
      .get("corporates/list")
      .then(({ data: { data } }) => {
        this.corporatesList = data.map(x => {
          return {
            label: `${x.name} (${x.shortName})`,
            fieldName: x._id,
            value: true,
            exportUrl: `corporates/report/${x._id}`
          }
        })
      });

    this.loading = false;
  },
  computed: {
    driverFilter() {
      return this.$route.query.driver || null;
    },
    riderFilter() {
      return this.$route.query.passenger || null;
    },
    reportDownloadUrl() {
      return (
        "export-batch?" +
        Object.entries({
          ...Object.fromEntries(
            Object.entries({
              driver: this.driverId ? this.driverId : undefined,
              dispatcher: this.userId ? this.userId : undefined,
              passenger: this.passengerId ? this.passengerId : undefined,
              ...(this.range || {}),
            }).filter(([key, val]) => key && val)
          ),
        })
          .map(([key, value]) => [key, value].join("="))
          .join("&")
      );
    },
  },
  methods: {
    searchDriver(e) {
      if (e.target.value.trim() === "") {
        this.driverSearchResults = [];
        this.driverId = null;
        this.getTripRequests();
      } else {
        const name = e.target.value.trim();

        if (name) {
          this.driverSearchResults = [];
          this.$misc
            .get("drivers/search?vehicle=true&limit=5&q=" + name)
            .then(({ data }) => {
              this.driverSearchResults = data;
            })
            .catch((err) => console.log(err))
            .finally(() => (this.loading = false));
        } else {
          this.driverSearchResults = [];
        }
      }
    },

    searchUser(e) {
      if (e.target.value.trim() === "") {
        this.userSearchResults = [];
        this.userId = null;
        this.getTripRequests();
      } else {
        const name = e.target.value.trim();

        if (name) {
          this.userSearchResults = [];
          this.$misc
            .get("accounts/search?vehicle=true&limit=5&q=" + name)
            .then(({ data }) => {
              this.userSearchResults = data;
            })
            .catch((err) => console.log(err))
            .finally(() => (this.loading = false));
        } else {
          this.userSearchResults = [];
        }
      }
    },

    searchPassenger(e) {
      if (e.target.value.trim() === "") {
        this.passengerSearchResults = [];
        this.passengerId = null;
        this.getTripRequests();
      } else {
        const name = e.target.value.trim();

        if (name) {
          this.passengerSearchResults = [];
          this.$misc
            .get("users/search?vehicle=true&limit=5&q=" + name)
            .then(({ data }) => {
              this.passengerSearchResults = data;
            })
            .catch((err) => console.log(err))
            .finally(() => (this.loading = false));
        } else {
          this.passengerSearchResults = [];
        }
      }
    },

    selectDriver(index) {
      let driver = this.driverSearchResults[index];
      this.selectedDriverObj = driver;
      this.selectedDriver = `${driver.firstName} ${driver.lastName} - ${driver.phoneNumber}`;
      this.driverId = driver._id;
      this.driverSearchResults = [];
      const query = { ...this.$route.query, driver: this.driverId };
      this.$router.push({ query });
      this.getTripRequests();
    },

    selectUser(index) {
      let user = this.userSearchResults[index];
      this.selectedUserObj = user;
      this.selectedUser = `${user.firstName} ${user.lastName}`;
      this.userId = user._id;
      this.userSearchResults = [];
      this.getTripRequests();
    },

    selectPassenger(index) {
      let user = this.passengerSearchResults[index];
      this.selectedPassengerObj = user;
      this.selectedPassenger = `${user.firstName} ${user.lastName} - ${user.phoneNumber}`;
      this.passengerId = user._id;
      this.passengerSearchResults = [];
      const query = { ...this.$route.query, passenger: this.passengerId };
      this.$router.push({ query });

      this.getTripRequests();
    },

    formatDate(date) {
      // return moment(date).format("MMM D, YYYY HH:mm");
      return date ? date.replace("T", " ").slice(0, 16) : "";
    },
    toggleDropdown(index) {
      if (this.openDropdown === index) {
        this.openDropdown = false;
      } else {
        this.openDropdown = index;
      }
    },
    hideDropdown() {
      this.openDropdown = false;
    },
  },
};
</script>